import { useEffect, useState } from "react";
import API from "../../services/api";
import {
  Pagination,
  Modal,
  Input,
  Typography,
  Spin,
  Button,
  App,
  Empty,
} from "antd";
import {
  EyeOutlined,
  CheckCircleOutlined,
  CloseCircleOutlined,
} from "@ant-design/icons";
import { useNavigate } from "react-router-dom";

import "./AdminAdsApproval.css";

const { Title, Text } = Typography;

function AdminAdsApproval() {
  const [ads, setAds] = useState([]);
  const [loading, setLoading] = useState(true);
  const [currentPage, setCurrentPage] = useState(1);
  const [total, setTotal] = useState(0);
  const navigate = useNavigate();

  const [isRejectModalOpen, setIsRejectModalOpen] = useState(false);
  const [rejectReason, setRejectReason] = useState("");
  const [selectedAdId, setSelectedAdId] = useState(null);

  const { message } = App.useApp();

  /* ── Helpers ──────────────────────────────────────────────── */
  const openRejectModal = (id) => {
    setSelectedAdId(id);
    setIsRejectModalOpen(true);
  };

  const closeRejectModal = () => {
    setIsRejectModalOpen(false);
    setRejectReason("");
    setSelectedAdId(null);
  };

  /* ── Fetch ────────────────────────────────────────────────── */
  const fetchPendingAds = async (page = 1) => {
    try {
      setLoading(true);
      const res = await API.get(`/ads/pending/all?page=${page}&limit=15`);
      setAds(res.data.ads || []);
      setTotal(res.data.total || 0);
      setCurrentPage(res.data.currentPage || 1);
    } catch {
      message.error("Failed to load pending ads");
      setAds([]);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchPendingAds(currentPage);
  }, [currentPage]);

  /* ── Actions ──────────────────────────────────────────────── */
  const handleApprove = async (id) => {
    try {
      await API.put(`/ads/approve/${id}`);
      message.success("Ad approved successfully");
      fetchPendingAds(currentPage);
    } catch {
      message.error("Approval failed");
    }
  };

  const handleConfirmReject = async () => {
    try {
      await API.put(`/ads/reject/${selectedAdId}`, {
        reason: rejectReason,
      });
      message.success("Ad rejected with reason");
      closeRejectModal();
      fetchPendingAds(currentPage);
    } catch (err) {
      message.error(err.response?.data?.message || "Reject failed");
    }
  };

  /* ── Loader ───────────────────────────────────────────────── */
  if (loading) {
    return (
      <div className="aaa-loader-shell">
        <Spin size="large" />
      </div>
    );
  }

  /* ── Render ───────────────────────────────────────────────── */
  return (
    <div className="aaa-page-shell">
      {/* HEADER */}
      <div className="aaa-topbar">
        <div className="aaa-topbar__content">
          <Title level={3} className="aaa-topbar__title">
            Ads Approvals
          </Title>
          <Text className="aaa-topbar__subtitle">
            Review and approve submitted advertisements
          </Text>
        </div>

        <div className="aaa-topbar__count">
          <span className="aaa-topbar__count-number">{ads.length}</span>
          <span className="aaa-topbar__count-label">Pending Review</span>
        </div>
      </div>

      {/* CONTENT */}
      {ads.length === 0 ? (
        <Empty description="No pending ads" className="aaa-empty-state" />
      ) : (
        <div className="aaa-listing-grid">
          {ads.map((ad) => (
            <div
              key={ad._id}
              className="aaa-listing-card"
              onClick={() => navigate(`/ads/${ad.slug}`)}
            >
              {/* Image */}
              <div className="aaa-listing-card__media">
                <img
                  alt={ad.title}
                  src={ad.thumbnail || "https://placehold.co/400x250?text=No+Image"}
                  className="aaa-listing-card__image"
                />

                <button
                  className="aaa-listing-card__view-action"
                  aria-label="View advertisement"
                  onClick={(e) => {
                    e.stopPropagation();
                    navigate(`/ads/${ad.slug}`);
                  }}
                >
                  <EyeOutlined />
                </button>

                <div className="aaa-listing-card__status">
                  <span className="aaa-listing-card__status-dot" />
                  Pending
                </div>
              </div>

              {/* Body */}
              <div className="aaa-listing-card__body">
                <h5
                  className="aaa-listing-card__title"
                  title={ad.title}
                >
                  {ad.title}
                </h5>

                <div className="aaa-listing-card__price">
                  <span className="aaa-listing-card__currency">₹</span>
                  <span className="aaa-listing-card__amount">
                    {ad.pricing?.price?.toLocaleString() || 0}
                  </span>
                  <span className="aaa-listing-card__period">ad</span>
                </div>

                <div className="aaa-listing-card__meta">
                  <span>
                    {ad.location?.city}, {ad.location?.state}
                  </span>
                  <span className="aaa-listing-card__meta-separator">•</span>
                  <span>{ad.category || "N/A"}</span>
                  <span className="aaa-listing-card__meta-separator">•</span>
                  <span>{ad.visibility?.trafficLevel || "Standard"}</span>
                </div>

                <div className="aaa-listing-card__actions">
                  <Button
                    type="primary"
                    className="aaa-action-btn aaa-action-btn--approve"
                    icon={<CheckCircleOutlined />}
                    onClick={(e) => {
                      e.stopPropagation();
                      handleApprove(ad._id);
                    }}
                  >
                    Approve
                  </Button>

                  <Button
                    danger
                    className="aaa-action-btn aaa-action-btn--reject"
                    icon={<CloseCircleOutlined />}
                    onClick={(e) => {
                      e.stopPropagation();
                      openRejectModal(ad._id);
                    }}
                  >
                    Reject
                  </Button>
                </div>
              </div>
            </div>
          ))}
        </div>
      )}

      {/* PAGINATION */}
      {ads.length > 0 && (
        <div className="aaa-pagination-wrap">
          <Pagination
            current={currentPage}
            total={total}
            pageSize={15}
            onChange={(page) => setCurrentPage(page)}
            showSizeChanger={false}
          />
        </div>
      )}

      {/* REJECT MODAL */}
      <Modal
        title="Reject Advertisement"
        open={isRejectModalOpen}
        okText="Confirm Rejection"
        cancelText="Cancel"
        okButtonProps={{ disabled: !rejectReason.trim(), danger: true }}
        onOk={handleConfirmReject}
        onCancel={closeRejectModal}
        className="aaa-reject-modal"
      >
        <div className="aaa-reject-modal__body">
          <Text strong className="aaa-reject-modal__label">
            Rejection Reason
          </Text>

          <Input.TextArea
            rows={4}
            placeholder="Please provide a detailed reason for rejection..."
            value={rejectReason}
            onChange={(e) => setRejectReason(e.target.value)}
            className="aaa-reject-modal__textarea"
          />

          {!rejectReason.trim() && (
            <p className="aaa-reject-modal__error">
              Reason is required
            </p>
          )}
        </div>
      </Modal>
    </div>
  );
}

export default AdminAdsApproval;